import * as THREE from 'three';
import type { SkyforgeSchema, DecorationSpec, FXJSON, CameraJSON } from './schema';
import type { DaylightSkyConfig } from '../atmosphere/daylight-sky';
import type { CloudLayerConfig } from '../board/cloud-layer';
import { runtimeAssetUrl } from '../../assets/runtime-asset-url';

export type MapName = 'skyforge' | 'neon-district';

/** Returned by loadMap(). Plan 18 BuildBoardEnvCeremonyVfxConfig
 *  (build-board.ts:115-131) wires getLanterns/getCrystals/getFountainAnchors
 *  directly. Plan 19 ships LISTEN-side onSkyQuake only (D5). */
export interface MapHandle {
  readonly name: MapName;
  readonly schema: SkyforgeSchema;
  readonly theme: ThemeOverride;
  readonly fx: FXJSON;
  readonly camera: CameraJSON | null;
  getLanterns(): readonly THREE.Object3D[];
  getCrystals(): readonly THREE.Object3D[];
  getFountainAnchors(): readonly THREE.Vector3[];
  onSkyQuake(cb: (durationSeconds: number) => void): () => void;
  dispose(): void;
}

export interface ThemeOverride {
  readonly lightingRig: import('../lighting').LightingConfig;
  readonly fog: { color: number; density: number };
  readonly sky: DaylightSkyConfig;
  readonly clouds: CloudLayerConfig;
  /** Legacy color fields retained for older callers; analytic sky ignores them. */
  readonly skyTopColor: number;
  readonly skyBottomColor: number;
}

export class MapAssemblyError extends Error {
  constructor(
    public readonly reason:
      | 'invalid_board_json'
      | 'invalid_theme_json'
      | 'invalid_decorations_json'
      | 'glb_load_failed'
      | 'unknown_map',
    message: string,
  ) {
    super(message);
    this.name = 'MapAssemblyError';
  }
}

export type {
  SkyforgeSchema,
  ThemeJSON,
  DecorationsJSON,
  FXJSON,
  CameraJSON,
  CameraStateJSON,
  BridgeSpec,
  DecorationSpec,
} from './schema';

// ─────────────────────────────────────────────────────────────────────────────
// Decoration model registry (Task 3.1) — modelKey → GLB asset URL on disk.
// Asset existence verified via `ls assets/models/deco-*.glb` (11 files present).
// ─────────────────────────────────────────────────────────────────────────────
export const DECORATION_MODEL_REGISTRY = {
  'tree-birch': runtimeAssetUrl('/assets/models/deco-tree-birch.glb'),
  'tree-pine': runtimeAssetUrl('/assets/models/deco-tree-pine.glb'),
  'tree-dead': runtimeAssetUrl('/assets/models/deco-tree-dead.glb'),
  'rock-small': runtimeAssetUrl('/assets/models/deco-rock-small.glb'),
  'rock-medium': runtimeAssetUrl('/assets/models/deco-rock-medium.glb'),
  'rock-large': runtimeAssetUrl('/assets/models/deco-rock-large.glb'),
  'ruin-arch': runtimeAssetUrl('/assets/models/deco-ruin-arch.glb'),
  'ruin-pillar': runtimeAssetUrl('/assets/models/deco-ruin-pillar.glb'),
  lantern: runtimeAssetUrl('/assets/models/deco-lantern.glb'),
  'floating-chunk-small': runtimeAssetUrl('/assets/models/deco-floating-chunk-small.glb'),
  'floating-chunk-large': runtimeAssetUrl('/assets/models/deco-floating-chunk-large.glb'),
} as const satisfies Record<DecorationSpec['modelKey'], string>;
